import arCommon from "./ar/common.json";
import arEmail from "./ar/email.json";
import arNative from "./ar/native.json";
import arSandbox from "./ar/sandbox.json";
import arWeb from "./ar/web.json";
import enCommon from "./en/common.json";
import enEmail from "./en/email.json";
import enNative from "./en/native.json";
import enSandbox from "./en/sandbox.json";
import enWeb from "./en/web.json";
import esCommon from "./es/common.json";
import esEmail from "./es/email.json";
import esNative from "./es/native.json";
import esSandbox from "./es/sandbox.json";
import esWeb from "./es/web.json";
import htCommon from "./ht/common.json";
import htEmail from "./ht/email.json";
import htNative from "./ht/native.json";
import htSandbox from "./ht/sandbox.json";
import htWeb from "./ht/web.json";
import ruCommon from "./ru/common.json";
import ruEmail from "./ru/email.json";
import ruNative from "./ru/native.json";
import ruSandbox from "./ru/sandbox.json";
import ruWeb from "./ru/web.json";
import tlCommon from "./tl/common.json";
import tlEmail from "./tl/email.json";
import tlNative from "./tl/native.json";
import tlSandbox from "./tl/sandbox.json";
import tlWeb from "./tl/web.json";
import viCommon from "./vi/common.json";
import viEmail from "./vi/email.json";
import viNative from "./vi/native.json";
import viSandbox from "./vi/sandbox.json";
import viWeb from "./vi/web.json";
import zhCommon from "./zh/common.json";
import zhEmail from "./zh/email.json";
import zhNative from "./zh/native.json";
import zhSandbox from "./zh/sandbox.json";
import zhWeb from "./zh/web.json";

export const webI18nResources = {
  en: {
    common: enCommon,
    web: enWeb,
    sandbox: enSandbox,
  },
  es: {
    common: esCommon,
    web: esWeb,
    sandbox: esSandbox,
  },
  zh: {
    common: zhCommon,
    web: zhWeb,
    sandbox: zhSandbox,
  },
  vi: {
    common: viCommon,
    web: viWeb,
    sandbox: viSandbox,
  },
  ru: {
    common: ruCommon,
    web: ruWeb,
    sandbox: ruSandbox,
  },
  ar: {
    common: arCommon,
    web: arWeb,
    sandbox: arSandbox,
  },
  tl: {
    common: tlCommon,
    web: tlWeb,
    sandbox: tlSandbox,
  },
  ht: {
    common: htCommon,
    web: htWeb,
    sandbox: htSandbox,
  },
} as const;

export const nativeI18nResources = {
  en: {
    common: enCommon,
    native: enNative,
    sandbox: enSandbox,
  },
  es: {
    common: esCommon,
    native: esNative,
    sandbox: esSandbox,
  },
  zh: {
    common: zhCommon,
    native: zhNative,
    sandbox: zhSandbox,
  },
  vi: {
    common: viCommon,
    native: viNative,
    sandbox: viSandbox,
  },
  ru: {
    common: ruCommon,
    native: ruNative,
    sandbox: ruSandbox,
  },
  ar: {
    common: arCommon,
    native: arNative,
    sandbox: arSandbox,
  },
  tl: {
    common: tlCommon,
    native: tlNative,
    sandbox: tlSandbox,
  },
  ht: {
    common: htCommon,
    native: htNative,
    sandbox: htSandbox,
  },
} as const;

// Server-side email templates. Kept separate from the web/native resources
// because emails are rendered by the backend (not the client apps) and only
// need the `email` namespace.
export const emailI18nResources = {
  en: { email: enEmail },
  es: { email: esEmail },
  zh: { email: zhEmail },
  vi: { email: viEmail },
  ru: { email: ruEmail },
  ar: { email: arEmail },
  tl: { email: tlEmail },
  ht: { email: htEmail },
} as const;

// Helper type to extract keys from a JSON object with proper nesting
type ExtractKeysType<T, Prefix extends string = ""> = T extends object
  ? {
      [K in keyof T]: K extends string
        ? T[K] extends object
          ? T[K] extends readonly unknown[]
            ? `${Prefix}${K}`
            : ExtractKeysType<T[K], `${Prefix}${K}.`>
          : `${Prefix}${K}`
        : never;
    }[keyof T]
  : never;

export type WebI18nKeyType =
  | `web:${ExtractKeysType<typeof enWeb>}`
  | `common:${ExtractKeysType<typeof enCommon>}`
  | `sandbox:${ExtractKeysType<typeof enSandbox>}`;

export type NativeI18nKeyType =
  | `native:${ExtractKeysType<typeof enNative>}`
  | `common:${ExtractKeysType<typeof enCommon>}`
  | `sandbox:${ExtractKeysType<typeof enSandbox>}`;

export type EmailI18nKeyType = ExtractKeysType<typeof enEmail>;

export type LanguageType =
  | "en"
  | "es"
  | "zh"
  | "vi"
  | "ru"
  | "ar"
  | "tl"
  | "ht";

export const SUPPORTED_LANGUAGES: LanguageType[] = [
  "en",
  "es",
  "zh",
  "vi",
  "ru",
  "ar",
  "tl",
  "ht",
];

// Right-to-left languages need `dir="rtl"` when rendering markup (e.g. emails).
export const RTL_LANGUAGES: LanguageType[] = ["ar"];

export const isRTL = (language: LanguageType): boolean =>
  RTL_LANGUAGES.includes(language);
